import React, { useCallback, useEffect, useState } from "react";
import Header from "./layout/Header";
import Footer from "./layout/Footer";
import Navigation from "./layout/Navigation";
import ModalManager from "./modals/ModalManager";
import { ProjectProvider, useProjectContext } from "../hooks/useProjectContext";
import ProjectsListView from "./views/global/ProjectsListView";
import MyProjectsView from "./views/global/MyProjectsView";
import GlobalAnalyticsView from "./views/global/GlobalAnalyticsView";
import GlobalDashboardView from "./views/global/GlobalDashboardView";
import GlobalAuditLogView from "./views/global/GlobalAuditLogView";
import UsersView from "./views/global/UsersView";
import BugReportsAdminView from "./views/global/BugReportsAdminView";
import GlobalAIUsageView from "./views/global/GlobalAIUsageView";
import ReportBugModal, { ReportBugButton } from "./modals/ReportBugModal";
import { projectsAPI } from "../services/api";
import ProjectLayout from "./views/project/ProjectLayout";
import { useGlobalDashboard } from "../hooks/useGlobalDashboard";
import { ncrAPI } from "../services/api";
import { isSystemAdmin } from "../constants/systemRoles";
import useEngagementTracking from "../hooks/useEngagementTracking";

const SmokyQAppInner = ({ currentUser, onLogout }) => {
  const isAdmin = isSystemAdmin(currentUser?.role);

  const {
    activeProject,
    setActiveProject,
    projectRole,
    setProjectRole,
    setCurrentUser,
    clearProject,
  } = useProjectContext();
  useEffect(() => {
    setCurrentUser(currentUser || null);
  }, [currentUser, setCurrentUser]);

  const [activeTab, setActiveTab] = useState("projects");
  const [reportBugOpen, setReportBugOpen] = useState(false);

  const [activeProjectTab, setActiveProjectTab] = useState("dashboard");
  useEngagementTracking({
    userId: currentUser?.id,
    projectId: activeProject?.id || null,
    module: activeProject ? activeProjectTab : `global-${activeTab}`,
  });
  const [modalType, setModalType] = useState(null);
  const [modalProps, setModalProps] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectsRefreshKey, setProjectsRefreshKey] = useState(0);

  const closeModal = () => {
    setModalType(null);
    setModalProps({});
  };

  const {
    dashboardData,
    kpis,
    projects,
    filteredNcrs,
    activityFeed,
    charts,
    isLoading: dashboardLoading,
    error: dashboardError,
    reload: reloadDashboard,
  } = useGlobalDashboard();

  const handleModalSubmit = async (payload) => {
    if (payload && payload.preventDefault) return;

    setIsSubmitting(true);
    try {
      if (modalType === "project") {
        await projectsAPI.create(payload);
        setProjectsRefreshKey((k) => k + 1);
      }
      closeModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuickAction = (type) => {
    switch (type) {
      case "project":
        setModalType("project");
        break;
      default:
        console.warn("Unknown quick action:", type);
    }
  };

  const handleUpdateNCRStatus = async (ncrId, nextStatus) => {
    try {
      await ncrAPI.updateStatus(ncrId, nextStatus);
      reloadDashboard?.();
    } catch (err) {
      console.error("Failed to update NCR status", err);
    }
  };

  const handleSelectProject = async (project) => {
    // Resolve the project role before mounting ProjectLayout so role-specific
    // users (notably third_party) never briefly render the default dashboard.
    if (isAdmin) {
      setProjectRole(null);
      setActiveProjectTab("dashboard");
      setActiveProject(project);
      return;
    }

    try {
      const res = await projectsAPI.getMyRole(project.id);
      const nextProjectRole = res?.data?.project_role || null;
      const nextProjectTab =
        String(nextProjectRole || "").toLowerCase() === "third_party"
          ? "equipment"
          : "dashboard";

      setProjectRole(nextProjectRole);
      setActiveProjectTab(nextProjectTab);
      setActiveProject(project);
    } catch (e) {
      console.error("Failed to load project role:", e);
      setProjectRole(null);
      setActiveProjectTab("dashboard");
      setActiveProject(project);
    }
  };


  const handleNotificationNavigate = useCallback(
    async (target = {}, notification = null) => {
      const projectId = target?.projectId;
      let nextProject = null;
      let effectiveProjectRole = projectRole;

      if (projectId) {
        nextProject =
          (Array.isArray(projects)
            ? projects.find((project) => Number(project.id) === Number(projectId))
            : null) || null;

        if (!nextProject) {
          try {
            const res = await projectsAPI.getOne(projectId);
            nextProject = res?.data || null;
          } catch (err) {
            console.error("Failed to load notification project:", err);
          }
        }

        if (nextProject) {
          setActiveProject(nextProject);
          try {
            if (isAdmin) {
              setProjectRole(null);
            } else {
              const roleRes = await projectsAPI.getMyRole(nextProject.id);
              effectiveProjectRole = roleRes?.data?.project_role || null;
              setProjectRole(effectiveProjectRole);
            }
          } catch (err) {
            console.error("Failed to load project role for notification:", err);
            setProjectRole(null);
          }
        }
      }

      const normalizedTarget = { ...(target || {}) };

      if (isAdmin && normalizedTarget.action === "openBugReport") {
        clearProject();
        setActiveTab("bug-reports");
        return;
      }

      const roleKey = (effectiveProjectRole || "").toLowerCase();
      const systemRoleKey = (currentUser?.role || "").toLowerCase();
      const isExternalIssueUser =
        systemRoleKey === "user_external" ||
        ["foreman", "contractor", "client"].includes(roleKey);

      if (normalizedTarget.action === "openFinding" || normalizedTarget.findingId) {
        // Assigned issue response queue is for foremen/external assignees only.
        // Admin/internal reviewers (QA/QC, inspectors, PMs, supervisors) should
        // review answered issues from the inspection drawer's Findings tab.
        normalizedTarget.tab = isExternalIssueUser ? "my-issues" : "inspections";
        normalizedTarget.action = "openFinding";
        normalizedTarget.drawerTab = "findings";
      } else if (
        normalizedTarget.action === "openInspection" &&
        isExternalIssueUser
      ) {
        normalizedTarget.tab = "assigned-inspections";
      }

      const detail = {
        ...normalizedTarget,
        notificationId: notification?.id,
      };

      const defaultProjectTab =
        String(effectiveProjectRole || "").toLowerCase() === "third_party"
          ? "equipment"
          : "dashboard";
      setActiveProjectTab(normalizedTarget?.tab || defaultProjectTab);

      // Give React a tick to mount the requested project tab before child views
      // receive the direct-open event.
      window.setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("smokyq:notification-open", { detail }),
        );
      }, 80);
    },
    [currentUser?.role, isAdmin, projectRole, projects, setActiveProject, setProjectRole, clearProject],
  );

  const renderAdminGlobalView = () => {
    switch (activeTab) {
      case "dashboard":
        return (
          <GlobalDashboardView
            dashboardData={dashboardData}
            kpis={kpis}
            projects={projects}
            filteredNcrs={filteredNcrs}
            activityFeed={activityFeed}
            currentUser={currentUser}
            isLoading={dashboardLoading}
            error={dashboardError}
            charts={charts}
            onQuickAction={handleQuickAction}
            onUpdateNCRStatus={handleUpdateNCRStatus}
            onOpenProject={handleSelectProject}
          />
        );
      case "analytics":
        return (
          <GlobalAnalyticsView
            kpis={kpis}
            charts={charts}
            isLoading={dashboardLoading}
            error={dashboardError}
          />
        );
      case "projects":
        return (
          <ProjectsListView
            currentUser={currentUser}
            refreshKey={projectsRefreshKey}
            onSelectProject={handleSelectProject}
            onCreateProject={() => setModalType("project")}
          />
        );
      case "users":
        return <UsersView />;
      case "audit-logs":
        return <GlobalAuditLogView />;
      case "bug-reports":
        return <BugReportsAdminView />;
      case "ai-usage":
        return <GlobalAIUsageView />;
      default:
        return <div className="p-6 text-gray-500">Not implemented</div>;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header
        currentUser={currentUser}
        activeProject={activeProject}
        onLogout={onLogout}
        onExitProject={clearProject}
        onNotificationNavigate={handleNotificationNavigate}
      />
      {!activeProject && isAdmin && (
        <Navigation
          role={currentUser?.role}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          scope="global"
        />
      )}

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-6">
        {activeProject ? (
          <ProjectLayout
            currentUser={currentUser}
            activeProject={activeProject}
            activeProjectTab={activeProjectTab}
            setActiveProjectTab={setActiveProjectTab}
            projectRole={projectRole}
          />
        ) : isAdmin ? (
          renderAdminGlobalView()
        ) : (
          <MyProjectsView onSelectProject={handleSelectProject} />
        )}
      </main>

      <Footer />

      <ReportBugButton onClick={() => setReportBugOpen(true)} />
      <ReportBugModal
        open={reportBugOpen}
        onClose={() => setReportBugOpen(false)}
        currentUser={currentUser}
        activeProject={activeProject}
        projectRole={projectRole}
      />

      <ModalManager
        modalType={modalType}
        modalProps={modalProps}
        onClose={closeModal}
        onSubmit={handleModalSubmit}
        isLoading={isSubmitting}
      />
    </div>
  );
};

const SmokyQApp = ({ currentUser, onLogout }) => {
  return (
    <ProjectProvider>
      <SmokyQAppInner currentUser={currentUser} onLogout={onLogout} />
    </ProjectProvider>
  );
};

export default SmokyQApp;
